import React, { useMemo, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldAlert, ArrowUpRight } from 'lucide-react';
import { useAppSelector, selectLoggedInUser, isOrganisationWithBilling } from '@app.raytd.com/store';
import { isRoleGreaterThanOrEqual } from '@/app/utils/roles';
import { useNavigate } from 'react-router-dom';
import FeedbackDialog from '@/components/raytd/feedback-dialog';
import { featureFlags } from '@/config/features';

const UpgradeCard: React.FC = () => {
  const navigate = useNavigate();
  const loggedInUser = useAppSelector(selectLoggedInUser);
  const billingEnabled = featureFlags.billingEnabled();

  const handleUpgrade = useCallback(() => {
    if (!billingEnabled) {
      // No self-serve billing page when billing is disabled — contact us instead.
      window.location.href = 'mailto:sales@raytd.com?subject=Restore%20Access%20Inquiry';
      return;
    }
    navigate('/organisation/billing');
  }, [billingEnabled, navigate]);

  // Determine what to show based on billing status
  const cardContent = useMemo(() => {
    // Safety checks
    if (!loggedInUser?.company) {
      return { type: 'beta' as const };
    }

    const company = loggedInUser.company;
    if (!isOrganisationWithBilling(company)) {
      return { type: 'beta' as const };
    }

    const { billing } = company;
    const { is_subscribed, on_trial, trial_ends_at, cancellation_pending, subscription_ends_at } = billing;

    // Check if user is admin
    const isAdmin = isRoleGreaterThanOrEqual(
      (company.role || 'guest') as 'admin' | 'member' | 'guest',
      'admin'
    );

    // Show upgrade prompt if trial expired or subscription cancelled
    const isExpiredTrial = !is_subscribed && !on_trial && trial_ends_at;
    const isCancelledSubscription = !is_subscribed && !on_trial && (cancellation_pending || subscription_ends_at);

    if (isExpiredTrial || isCancelledSubscription) {
      return {
        type: 'upgrade' as const,
        isAdmin,
        isExpiredTrial,
        isCancelledSubscription,
        organisationName: company.title,
      };
    }

    // Default: show beta program
    return { type: 'beta' as const };
  }, [loggedInUser]);

  // Show upgrade prompt when trial expired or subscription cancelled
  if (cardContent.type === 'upgrade') {
    const { isAdmin, isExpiredTrial, isCancelledSubscription, organisationName } = cardContent;

    return (
      <Card className="shadow-none border border-zinc-200 bg-zinc-50">
        <CardHeader className="p-2 pt-0 md:p-4">
          <CardDescription className="text-xs text-zinc-600">
            {isExpiredTrial ? 'Trial Period Ended' : 'Subscription Cancelled'}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-2 pt-0 md:p-4 md:pt-0 space-y-3">
          {isAdmin ? (
            // ADMIN VIEW: Actionable
            <>
              <p className="text-xs text-zinc-500 leading-relaxed">
                {isExpiredTrial
                  ? 'Your trial has expired. Upgrade to restore access.'
                  : 'Your subscription has been cancelled. Upgrade to restore access.'}
              </p>
              <Button
                onClick={handleUpgrade}
                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white text-xs h-8"
                size="sm"
              >
                {billingEnabled ? 'Upgrade Plan' : 'Contact us'}
                <ArrowUpRight className="h-3 w-3 ml-1.5" />
              </Button>
            </>
          ) : (
            // MEMBER VIEW: Informational only
            <>
              <p className="text-xs text-zinc-500 leading-relaxed">
                {isExpiredTrial
                  ? `The trial for ${organisationName} has expired. Access is restricted.`
                  : `The subscription for ${organisationName} has been cancelled. Access is restricted.`}
              </p>
              <div className="bg-zinc-100 border border-zinc-200 rounded-md p-2 flex items-center gap-2 text-xs text-zinc-600">
                <ShieldAlert className="h-3 w-3 text-zinc-400 shrink-0" />
                <span>Contact your Organization Admin to restore access.</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  // Default: Show beta program
  return (
    <Card className="shadow-none border-none bg-zinc-100">
      <CardHeader className="p-2 pt-0 md:p-4">
        <CardDescription>
          You are enrolled in our Raytd.Beta program
        </CardDescription>
      </CardHeader>
      <CardContent className="p-2 pt-0 md:p-4 md:pt-0">
        <FeedbackDialog />
      </CardContent>
    </Card>
  );
};

export default UpgradeCard;
